import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import "./css/NavBar.css";
import Dropdown from "./common/Dropdown";
import { Badge, Dropdown as BootstrapDropdown } from "react-bootstrap";
import { FaBell, FaEnvelope } from "react-icons/fa";
import { FiShield, FiUser } from "react-icons/fi";
import * as signalR from "@microsoft/signalr";
import { useNavigate } from "react-router-dom";
import { useGetUserNotification } from "../api/queries/useGetUserNotification";
import { useGetMessages } from "../api/queries/useGetMessages";
import { useSaveNotification } from "../api/mutations/useSaveNotification";
import { useAppContext } from "../Context/AppContext";
import {
  clearTokens,
  isLoggedIn,
  getAccessToken as readAccessToken,
} from "../auth/authStorage";
import { clearAuthSensitiveQueries } from "../auth/authSession";
import { useMembershipSidebarGate } from "../hooks/useMembershipSidebarGate";
import LiveTranslationDropdown from "./common/LiveTranslationDropdown";
import { useTranslation } from "react-i18next";
import { Message, Notification } from "../types/Types";
import { getNotificationHubUrl } from "../api/axiosInstance";
import {
  getConversationUnreadCount,
  getLastIncomingMessageDate,
  getUnreadMessageCount,
  HEADER_MESSAGE_READ_EVENT,
  loadMessageReadState,
  markConversationAsRead,
  type MessageReadState,
} from "../utils/headerInboxState";
import {
  isMessageNotification,
  resolveNotificationPath,
} from "../utils/notificationRouting";

interface HeaderIconButtonProps {
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  count: number;
  ariaLabel: string;
  icon: "notification" | "message";
}

type HeaderMessageConversation = {
  id: number;
  firstName: string;
  lastName: string;
  messages: Message[];
};

function formatRelativeTime(value?: string | number | Date | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const diffMs = Date.now() - date.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diffMs < minute) return "Just now";
  if (diffMs < hour) return `${Math.floor(diffMs / minute)}m`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h`;
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)}d`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function HeaderIconButton({
  onClick,
  count,
  ariaLabel,
  icon,
}: HeaderIconButtonProps) {
  return (
    <button
      onClick={onClick}
      className="btn btn-link text-dark text-decoration-none position-relative p-2 rounded-circle"
      aria-label={ariaLabel}
      type="button"
    >
      {icon === "notification" ? <FaBell size={18} /> : <FaEnvelope size={18} />}
      {count > 0 && (
        <Badge
          bg="danger"
          pill
          className="position-absolute top-0 start-100 translate-middle"
          style={{ fontSize: "0.6rem" }}
        >
          {count > 9 ? "9+" : count}
        </Badge>
      )}
    </button>
  );
}

function HoverCard({
  show,
  setShow,
  button,
  children,
  id,
  onOpen,
}: {
  show: boolean;
  setShow: (value: boolean) => void;
  button: React.ReactNode;
  children: React.ReactNode;
  id: string;
  onOpen?: () => void;
}) {
  return (
    <BootstrapDropdown
      show={show}
      onToggle={(nextShow) => {
        if (nextShow) onOpen?.();
        setShow(nextShow);
      }}
      align="end"
    >
      <BootstrapDropdown.Toggle as="div" id={id}>
        {button}
      </BootstrapDropdown.Toggle>
      <BootstrapDropdown.Menu
        className="shadow border-0 rounded-3 p-0 mt-2"
        style={{ width: "min(360px, calc(100vw - 1.5rem))", maxHeight: 420 }}
        renderOnMount
      >
        {children}
      </BootstrapDropdown.Menu>
    </BootstrapDropdown>
  );
}

function Navbar() {
  const { t } = useTranslation();
  const isAuthenticated = isLoggedIn();
  const { user, viewMode, setViewMode } = useAppContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const objectId =
    typeof window !== "undefined" ? localStorage.getItem("objectId") ?? "" : "";
  const [showNotificationsCard, setShowNotificationsCard] = useState(false);
  const [showMessagesCard, setShowMessagesCard] = useState(false);
  const [messageReadState, setMessageReadState] = useState<MessageReadState>(() =>
    loadMessageReadState(objectId)
  );

  const hasPaid = user?.user?.hasPaid === 1;
  const { lockSidebarForMembership, treatAsSidebarMember } =
    useMembershipSidebarGate();
  const showMessagesNav = hasPaid || lockSidebarForMembership;
  const showNotificationsNav = showMessagesNav;

  const { data: notifications } = useGetUserNotification({
    id: user?.user?.id ?? 0,
    enabled: isAuthenticated && showNotificationsNav && !!user?.user?.id,
    refetchIntervalMs: 15000,
    pageSize: 50,
  });
  const { data: userMessages } = useGetMessages({
    id: objectId,
    enabled: isAuthenticated && showMessagesNav && !!objectId,
    refetchIntervalMs: 15000,
  });
  const { mutate: updateNotification } = useSaveNotification({
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["notification", user?.user?.id ?? 0],
      });
    },
  });

  const systemNotifications = useMemo(
    () =>
      [...(notifications?.data ?? [])]
        .filter((item: Notification) => !isMessageNotification(item))
        .sort(
          (a: Notification, b: Notification) =>
            new Date(b.date).getTime() - new Date(a.date).getTime()
        ),
    [notifications?.data]
  );

  const unreadNotificationCount = useMemo(
    () => systemNotifications.filter((item) => item.isRead === false).length,
    [systemNotifications]
  );

  const notificationPreviewItems = useMemo(
    () => systemNotifications.slice(0, 5),
    [systemNotifications]
  );

  const messageConversations = useMemo<HeaderMessageConversation[]>(() => {
    if (!userMessages?.length) return [];

    const currentUserId = user?.user?.id;
    const allMessages = userMessages.flatMap((item) => item.messages ?? []);
    if (!allMessages.length) return [];

    const peerIds = new Set<number>();
    allMessages.forEach((message) => {
      const fromId = Number(message.userIdMessageFrom);
      const toId = Number(message.userIdMessageTo);
      if (currentUserId != null) {
        if (fromId !== Number(currentUserId)) peerIds.add(fromId);
        if (toId !== Number(currentUserId)) peerIds.add(toId);
      } else {
        peerIds.add(fromId);
        peerIds.add(toId);
      }
    });

    return Array.from(peerIds)
      .map((peerId) => {
        const group = userMessages.find((item) =>
          item.messages?.some(
            (message) =>
              Number(message.userIdMessageFrom) === peerId ||
              Number(message.userIdMessageTo) === peerId
          )
        );
        const messages = allMessages
          .filter(
            (message) =>
              Number(message.userIdMessageFrom) === peerId ||
              Number(message.userIdMessageTo) === peerId
          )
          .sort(
            (a, b) =>
              new Date(a.dateSent).getTime() - new Date(b.dateSent).getTime()
          );

        return {
          id: peerId,
          firstName: group?.firstName || "",
          lastName: group?.lastName || "",
          messages,
        };
      })
      .filter((conversation) => conversation.messages.length > 0)
      .sort((a, b) => {
        const aDate = a.messages[a.messages.length - 1]?.dateSent || "";
        const bDate = b.messages[b.messages.length - 1]?.dateSent || "";
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      });
  }, [userMessages, user?.user?.id]);

  const unreadMessageCount = useMemo(
    () =>
      getUnreadMessageCount(
        messageConversations,
        user?.user?.id,
        messageReadState
      ),
    [messageConversations, user?.user?.id, messageReadState]
  );

  const messagePreviewItems = useMemo(
    () => messageConversations.slice(0, 5),
    [messageConversations]
  );

  useEffect(() => {
    setMessageReadState(loadMessageReadState(objectId));
  }, [objectId]);

  useEffect(() => {
    const syncReadState = () => setMessageReadState(loadMessageReadState(objectId));
    const handleStorage = (event: StorageEvent) => {
      if (!event.key || event.key.includes("header.messageReadState")) {
        syncReadState();
      }
    };

    window.addEventListener(HEADER_MESSAGE_READ_EVENT, syncReadState);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(HEADER_MESSAGE_READ_EVENT, syncReadState);
      window.removeEventListener("storage", handleStorage);
    };
  }, [objectId]);

  useEffect(() => {
    if (!isAuthenticated || !objectId || !user?.user?.id) return;

    const token = readAccessToken();
    if (!token) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(getNotificationHubUrl(), {
        accessTokenFactory: async () => token,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on("NotificationChanged", (payload?: { userId?: number }) => {
      if (payload?.userId && payload.userId !== user.user.id) return;
      void queryClient.invalidateQueries({
        queryKey: ["notification", user.user.id],
      });
    });

    connection.on("MessageInboxChanged", (payload?: { userId?: number }) => {
      if (payload?.userId && payload.userId !== user.user.id) return;
      void queryClient.invalidateQueries({
        queryKey: ["getMessages", objectId],
      });
    });

    void connection.start().catch((error) => {
      console.error("Notification hub connection error:", error);
    });

    return () => {
      connection.off("NotificationChanged");
      connection.off("MessageInboxChanged");
      void connection.stop();
    };
  }, [isAuthenticated, objectId, queryClient, user?.user?.id]);

  const handleLogout = useCallback(async () => {
    clearAuthSensitiveQueries(queryClient);
    clearTokens();
    navigate("/", { replace: true });
  }, [navigate, queryClient]);

  const handleNavigateToMessages = useCallback(() => {
    setShowMessagesCard(false);
    navigate("/messages");
  }, [navigate]);

  const handleNavigateToNotifications = useCallback(() => {
    setShowNotificationsCard(false);
    navigate("/notifications");
  }, [navigate]);

  const handleToggleMessagesCard = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setShowNotificationsCard(false);
      setShowMessagesCard((prev) => !prev);
    },
    []
  );

  const handleToggleNotificationsCard = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setShowMessagesCard(false);
      setShowNotificationsCard((prev) => !prev);
    },
    []
  );

  const handleNavigateToAccount = useCallback(() => {
    navigate("/my-account");
  }, [navigate]);

  const handleNotificationItemClick = useCallback(
    (notification: Notification) => {
      setShowNotificationsCard(false);
      if (!notification.isRead) {
        updateNotification({ ...notification, isRead: true });
      }
      navigate(resolveNotificationPath(notification));
    },
    [navigate, updateNotification]
  );

  const handleMessageItemClick = useCallback(
    (conversation: HeaderMessageConversation) => {
      setShowMessagesCard(false);
      markConversationAsRead(
        objectId,
        conversation.id,
        getLastIncomingMessageDate(conversation, user?.user?.id)
      );
      setMessageReadState(loadMessageReadState(objectId));
      navigate(`/messages/${conversation.id}`);
    },
    [navigate, objectId, user?.user?.id]
  );

  const isAdmin = user?.user?.isAdmin === 1;

  const handleToggleViewMode = useCallback(() => {
    if (viewMode === "admin") {
      setViewMode("member");
      navigate("/dashboard");
      return;
    }
    setViewMode("admin");
    navigate("/admin", { state: { adminTab: "events" } });
  }, [viewMode, setViewMode, navigate]);

  const userProfileImage = useMemo(
    () =>
      user?.imageUrls?.PersonalProfessionalPhoto ||
      user?.imageUrls?.ProfessionalPhoto ||
      "/user.png",
    [
      user?.imageUrls?.PersonalProfessionalPhoto,
      user?.imageUrls?.ProfessionalPhoto,
    ]
  );

  const memberBenefits = useMemo(
    () => [
      { title: t("nav.businessMembership"), href: "business-membership" },
      { title: t("nav.executiveMembership"), href: "executive-membership" },
      { title: t("nav.earlyStageMembership"), href: "early-stage-membership" },
    ],
    [t]
  );
  const internationalTrades = useMemo(
    () => [
      {
        title: t("nav.executiveInternationalMembership"),
        href: "executive-international-membership",
      },
      {
        title: t("nav.saudiTradeProgramme"),
        href: "saudi-arabia-international-trade-programme",
      },
      {
        title: t("nav.franceDelegation"),
        href: "france-business-delegation-2025",
      },
      {
        title: t("nav.turkiyeTradeProgramme"),
        href: "smbf-turkiye-international-trade-programme",
      },
      {
        title: t("nav.dubaiDelegation"),
        href: "dubai-business-delegation",
      },
    ],
    [t]
  );

  const notificationCard = (
    <>
      <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
        <span className="fw-semibold">{t("notifications.title")}</span>
        {unreadNotificationCount > 0 && (
          <Badge bg="primary" pill>
            {unreadNotificationCount} new
          </Badge>
        )}
      </div>
      <div className="overflow-y-auto overflow-x-hidden" style={{ maxHeight: 300 }}>
        {notificationPreviewItems.length === 0 ? (
          <div className="text-center text-muted small py-4 px-3">
            {t("notifications.empty")}
          </div>
        ) : (
          notificationPreviewItems.map((notification) => {
            const title = notification.name || t("notifications.title");
            return (
              <button
                key={notification.id}
                type="button"
                className={`btn btn-light rounded-0 text-start w-100 border-0 border-bottom d-flex align-items-start gap-2 px-3 py-2 overflow-hidden ${
                  notification.isRead ? "" : "bg-primary-subtle"
                }`}
                onClick={() => handleNotificationItemClick(notification)}
              >
                <div
                  className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0 fw-semibold"
                  style={{ width: 40, height: 40, fontSize: "0.75rem" }}
                >
                  {getInitials(title)}
                </div>
                <div className="min-w-0 flex-grow-1 overflow-hidden">
                  <div className="d-flex justify-content-between gap-2 min-w-0">
                    <span className="fw-semibold text-truncate small min-w-0">
                      {title}
                    </span>
                    <small className="text-muted flex-shrink-0">
                      {formatRelativeTime(notification.date)}
                    </small>
                  </div>
                  <div className="small text-muted text-truncate">
                    {notification.description}
                  </div>
                </div>
                {!notification.isRead && (
                  <span
                    className="rounded-circle bg-primary flex-shrink-0 mt-2"
                    style={{ width: 8, height: 8 }}
                  />
                )}
              </button>
            );
          })
        )}
      </div>
      <button
        type="button"
        className="btn btn-link text-decoration-none w-100 rounded-0 border-0 border-top py-2 small fw-semibold"
        onClick={handleNavigateToNotifications}
      >
        View all notifications
      </button>
    </>
  );

  const messageCard = (
    <>
      <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
        <span className="fw-semibold">{t("sidebar.messages")}</span>
        {unreadMessageCount > 0 && (
          <Badge bg="primary" pill>
            {unreadMessageCount} new
          </Badge>
        )}
      </div>
      <div className="overflow-y-auto overflow-x-hidden" style={{ maxHeight: 300 }}>
        {messagePreviewItems.length === 0 ? (
          <div className="text-center text-muted small py-4 px-3">
            No messages yet
          </div>
        ) : (
          messagePreviewItems.map((conversation) => {
            const unread = getConversationUnreadCount(
              conversation,
              user?.user?.id,
              messageReadState
            );
            const lastMessage =
              conversation.messages[conversation.messages.length - 1];
            const fullName =
              `${conversation.firstName} ${conversation.lastName}`.trim() ||
              "Conversation";

            return (
              <button
                key={conversation.id}
                type="button"
                className={`btn btn-light rounded-0 text-start w-100 border-0 border-bottom d-flex align-items-start gap-2 px-3 py-2 overflow-hidden ${
                  unread > 0 ? "bg-primary-subtle" : ""
                }`}
                onClick={() => handleMessageItemClick(conversation)}
              >
                <div
                  className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0 fw-semibold"
                  style={{ width: 40, height: 40, fontSize: "0.75rem" }}
                >
                  {getInitials(fullName)}
                </div>
                <div className="min-w-0 flex-grow-1 overflow-hidden">
                  <div className="d-flex justify-content-between gap-2 min-w-0">
                    <span className="fw-semibold text-truncate small min-w-0">
                      {fullName}
                    </span>
                    <small className="text-muted flex-shrink-0">
                      {formatRelativeTime(lastMessage?.dateSent)}
                    </small>
                  </div>
                  <div className="small text-muted text-truncate">
                    {lastMessage?.body || ""}
                  </div>
                </div>
                {unread > 0 && (
                  <span
                    className="rounded-circle bg-primary flex-shrink-0 mt-2"
                    style={{ width: 8, height: 8 }}
                  />
                )}
              </button>
            );
          })
        )}
      </div>
      <button
        type="button"
        className="btn btn-link text-decoration-none w-100 rounded-0 border-0 border-top py-2 small fw-semibold"
        onClick={handleNavigateToMessages}
      >
        View all messages
      </button>
    </>
  );

  const renderViewModeToggle = () =>
    isAdmin ? (
      <button
        type="button"
        onClick={handleToggleViewMode}
        className="d-inline-flex align-items-center gap-2 p-0 border-0 bg-transparent shadow-none text-decoration-none fw-medium text-dark"
        title={
          viewMode === "admin"
            ? t("nav.switchToMember")
            : t("nav.switchToAdmin")
        }
        aria-label={
          viewMode === "admin"
            ? t("nav.switchToMember")
            : t("nav.switchToAdmin")
        }
      >
        <span
          className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: 16, height: 16, lineHeight: 1 }}
        >
          {viewMode === "admin" ? (
            <FiUser size={16} strokeWidth={2} aria-hidden />
          ) : (
            <FiShield size={16} strokeWidth={2} aria-hidden />
          )}
        </span>
        <span className="d-none d-md-inline">
          {viewMode === "admin"
            ? t("nav.switchToMember")
            : t("nav.switchToAdmin")}
        </span>
      </button>
    ) : null;

  const profileDropdown = (toggleId: string) => (
    <BootstrapDropdown align="end">
      <BootstrapDropdown.Toggle
        variant="link"
        className="rounded-circle p-0"
        id={toggleId}
      >
        <img
          src={userProfileImage}
          alt={t("nav.userProfileAlt")}
          className="rounded-circle"
          width={36}
          height={36}
          loading="lazy"
          decoding="async"
          style={{ objectFit: "cover" }}
        />
      </BootstrapDropdown.Toggle>
      <BootstrapDropdown.Menu>
        <BootstrapDropdown.Item onClick={handleNavigateToAccount}>
          {t("nav.profile")}
        </BootstrapDropdown.Item>
        <BootstrapDropdown.Item onClick={handleLogout}>
          {t("nav.logout")}
        </BootstrapDropdown.Item>
      </BootstrapDropdown.Menu>
    </BootstrapDropdown>
  );

  if (!isAuthenticated) {
    return (
      <nav className="navbar navbar-expand-lg navbar-light fixed-top">
        <div className="container">
          <a className="navbar-brand" href="/">
            <img
              src="/connect-logo.png"
              className="logo"
              alt={t("nav.logoAlt")}
              width={125}
              height={80}
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </a>
          <button
            className="navbar-toggler border border-white shadow-none"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
            aria-controls="navbarNav"
            aria-expanded="false"
            aria-label={t("nav.toggleNavigation")}
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav me-auto">
              <li className="nav-item text-center">
                <a className="nav-link" href="/our-vision">
                  {t("nav.ourVision")}
                </a>
              </li>
              <li className="nav-item text-center">
                <Dropdown
                  title={t("nav.memberBenefits")}
                  href="/members-benefits-smbf"
                  items={memberBenefits}
                />
              </li>
              <li className="nav-item text-center">
                <a className="nav-link" href="/events">
                  {t("nav.events")}
                </a>
              </li>
              <li className="nav-item text-center">
                <Dropdown
                  title={t("nav.internationalTrade")}
                  href="/international-business-set-up-programme"
                  items={internationalTrades}
                />
              </li>
              <li className="nav-item text-center">
                <a className="nav-link" href="/support-our-growth">
                  {t("nav.supportUs")}
                </a>
              </li>
            </ul>
            <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-2 ms-lg-auto py-2 py-lg-0">
              <LiveTranslationDropdown />
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="navbar navbar-expand-lg navbar-light fixed-top p-0">
      <div className="d-flex justify-content-between w-100 p-0">
        <div
          className={`d-flex flex-lg-row justify-content-between px-1 px-md-4 ${
            treatAsSidebarMember ? "nav-branding" : ""
          }`}
        >
          <a
            className="navbar-brand py-0 my-0"
            href={viewMode === "admin" && isAdmin ? "/admin" : "/dashboard"}
            onClick={(e) => {
              e.preventDefault();
              if (viewMode === "admin" && isAdmin) {
                navigate("/admin", { state: { adminTab: "events" } });
                return;
              }
              navigate("/dashboard");
            }}
          >
            <img
              src="/connect-logo.png"
              className="logo"
              alt={t("nav.logoAlt")}
              width={125}
              height={80}
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </a>
          {treatAsSidebarMember && (
            <div className="d-flex d-lg-none align-items-center gap-2 gap-sm-3">
              <LiveTranslationDropdown />
              {renderViewModeToggle()}
              {showMessagesNav && (
                <HoverCard
                  id="header-messages-mobile"
                  show={showMessagesCard}
                  setShow={setShowMessagesCard}
                  onOpen={() => setShowNotificationsCard(false)}
                  button={
                    <HeaderIconButton
                      onClick={handleToggleMessagesCard}
                      count={unreadMessageCount}
                      icon="message"
                      ariaLabel={t("sidebar.messages")}
                    />
                  }
                >
                  {messageCard}
                </HoverCard>
              )}
              {showNotificationsNav && (
                <HoverCard
                  id="header-notifications-mobile"
                  show={showNotificationsCard}
                  setShow={setShowNotificationsCard}
                  onOpen={() => setShowMessagesCard(false)}
                  button={
                    <HeaderIconButton
                      onClick={handleToggleNotificationsCard}
                      count={unreadNotificationCount}
                      icon="notification"
                      ariaLabel={t("notifications.title")}
                    />
                  }
                >
                  {notificationCard}
                </HoverCard>
              )}
              {profileDropdown("nav-profile-mobile")}
            </div>
          )}
        </div>

        <div
          className={`align-items-center justify-content-end gap-4 w-100 px-4 ${
            treatAsSidebarMember ? "d-none d-lg-flex" : "d-flex"
          }`}
        >
          <LiveTranslationDropdown />
          {renderViewModeToggle()}
          {showMessagesNav && (
            <HoverCard
              id="header-messages-desktop"
              show={showMessagesCard}
              setShow={setShowMessagesCard}
              onOpen={() => setShowNotificationsCard(false)}
              button={
                <HeaderIconButton
                  onClick={handleToggleMessagesCard}
                  count={unreadMessageCount}
                  icon="message"
                  ariaLabel={t("sidebar.messages")}
                />
              }
            >
              {messageCard}
            </HoverCard>
          )}
          {showNotificationsNav && (
            <HoverCard
              id="header-notifications-desktop"
              show={showNotificationsCard}
              setShow={setShowNotificationsCard}
              onOpen={() => setShowMessagesCard(false)}
              button={
                <HeaderIconButton
                  onClick={handleToggleNotificationsCard}
                  count={unreadNotificationCount}
                  icon="notification"
                  ariaLabel={t("notifications.title")}
                />
              }
            >
              {notificationCard}
            </HoverCard>
          )}
          {profileDropdown("nav-profile-desktop")}
        </div>
      </div>
    </nav>
  );
}

Navbar.displayName = "Navbar";

export default memo(Navbar);
